import type { Skill, SkillCategory } from '../types/experience'

export const skills: Skill[] = [
  // Frontend Development
  {
    id: 'flutter',
    name: 'Flutter',
    category: 'frontend',
    proficiency: 'expert',
    description: 'Cross-platform mobile apps for Android & iOS'
  },
  {
    id: 'react-native',
    name: 'React Native',
    category: 'frontend',
    proficiency: 'expert',
    description: 'Cross-platform mobile apps with Expo & Bare workflow'
  },
  {
    id: 'jetpack-compose',
    name: 'Jetpack Compose',
    category: 'frontend',
    proficiency: 'advanced',
    description: 'Declarative modern UI toolkit for native Android'
  },
  {
    id: 'getx',
    name: 'GetX State Management',
    category: 'frontend',
    proficiency: 'expert',
    description: 'State management, dependency injection, and routing for Flutter'
  },
  {
    id: 'riverpod',
    name: 'Riverpod',
    category: 'frontend',
    proficiency: 'advanced',
    description: 'Compile-safe, reactive state management for Flutter'
  },
  {
    id: 'vue',
    name: 'Vue.js',
    category: 'frontend',
    proficiency: 'expert',
    description: 'Vue 3, Composition API, Pinia, Vue Router'
  },
  {
    id: 'react',
    name: 'React',
    category: 'frontend',
    proficiency: 'advanced',
    description: 'React components, hooks, state management'
  },
  {
    id: 'tailwind',
    name: 'Tailwind CSS',
    category: 'frontend',
    proficiency: 'advanced',
    description: 'Utility-first styling & custom design systems'
  },
  {
    id: 'vite',
    name: 'Vite',
    category: 'frontend',
    proficiency: 'advanced',
    description: 'Modern build tooling & HMR setup'
  },
  {
    id: 'pinia',
    name: 'Pinia',
    category: 'frontend',
    proficiency: 'expert',
    description: 'State management for Vue 3 applications'
  },
  {
    id: 'shadcn-vue',
    name: 'Shadcn-Vue',
    category: 'frontend',
    proficiency: 'advanced',
    description: 'Accessible & re-usable UI components'
  },

  // Backend Development
  {
    id: 'nodejs',
    name: 'Node.js',
    category: 'backend',
    proficiency: 'expert',
    description: 'Asynchronous event-driven server applications'
  },
  {
    id: 'nestjs',
    name: 'NestJS',
    category: 'backend',
    proficiency: 'expert',
    description: 'Scalable server-side architecture with TypeScript'
  },
  {
    id: 'express',
    name: 'Express.js',
    category: 'backend',
    proficiency: 'advanced',
    description: 'REST API routing & middleware creation'
  },
  {
    id: 'rest-apis',
    name: 'REST APIs',
    category: 'backend',
    proficiency: 'expert',
    description: 'API design, specification, & integration'
  },
  {
    id: 'socketio',
    name: 'Socket.IO',
    category: 'backend',
    proficiency: 'advanced',
    description: 'Real-time bidirectional event-based communication'
  },
  {
    id: 'auth',
    name: 'Authentication & Authorization',
    category: 'backend',
    proficiency: 'advanced',
    description: 'JWT, OAuth, RBAC & PBAC security policies'
  },
  {
    id: 'mvc',
    name: 'MVC Architecture',
    category: 'backend',
    proficiency: 'advanced',
    description: 'Model-View-Controller design pattern'
  },

  // Database & Data Management
  {
    id: 'room',
    name: 'Room SQLite',
    category: 'database',
    proficiency: 'advanced',
    description: 'Type-safe Android ORM with reactive Flow queries'
  },
  {
    id: 'realm',
    name: 'Realm DB',
    category: 'database',
    proficiency: 'advanced',
    description: 'Fast offline-first reactive mobile database for React Native'
  },
  {
    id: 'mongodb',
    name: 'MongoDB',
    category: 'database',
    proficiency: 'advanced',
    description: 'NoSQL document database, aggregation pipelines'
  },
  {
    id: 'mysql',
    name: 'MySQL',
    category: 'database',
    proficiency: 'advanced',
    description: 'Relational database management & SQL queries'
  },
  {
    id: 'mongoose',
    name: 'Mongoose',
    category: 'database',
    proficiency: 'advanced',
    description: 'Object Data Modeling (ODM) for MongoDB'
  },
  {
    id: 'prisma',
    name: 'Prisma ORM',
    category: 'database',
    proficiency: 'advanced',
    description: 'Next-generation ORM for Node.js & TypeScript'
  },
  {
    id: 'data-modeling',
    name: 'Data Modeling',
    category: 'database',
    proficiency: 'advanced',
    description: 'Designing scalable & efficient database schemas'
  },
  {
    id: 'query-optimization',
    name: 'Query Optimization',
    category: 'database',
    proficiency: 'advanced',
    description: 'Indexing, query performance, and profiling'
  },

  // DevOps & Deployment
  {
    id: 'git',
    name: 'Git',
    category: 'devops',
    proficiency: 'expert',
    description: 'Version control, branching strategies, rebase'
  },
  {
    id: 'github-actions',
    name: 'GitHub Actions',
    category: 'devops',
    proficiency: 'advanced',
    description: 'Automated CI/CD workflows and deployment pipelines'
  },
  {
    id: 'app-security',
    name: 'Mobile App Security',
    category: 'devops',
    proficiency: 'advanced',
    description: 'Google Play Integrity API, FreeRASP, and device attestation'
  },
  {
    id: 'nginx',
    name: 'Nginx',
    category: 'devops',
    proficiency: 'advanced',
    description: 'Reverse proxy, SSL setup, and static asset serving'
  },
  {
    id: 'docker',
    name: 'Docker',
    category: 'devops',
    proficiency: 'advanced',
    description: 'Containerization, Dockerfiles, and compose stacks'
  },
  {
    id: 'vps',
    name: 'VPS (Ubuntu)',
    category: 'devops',
    proficiency: 'advanced',
    description: 'Server configuration, security, and maintenance'
  },
  {
    id: 'cicd',
    name: 'CI/CD Pipelines',
    category: 'devops',
    proficiency: 'advanced',
    description: 'Automated testing, building, and deployment'
  },
  {
    id: 'pm2',
    name: 'PM2 Process Management',
    category: 'devops',
    proficiency: 'advanced',
    description: 'Production process manager for Node.js'
  },

  // Software Architecture
  {
    id: 'modular-design',
    name: 'Modular Design',
    category: 'architecture',
    proficiency: 'expert',
    description: 'Decoupled, reusable component & service architecture'
  },
  {
    id: 'offline-first',
    name: 'Offline-First Mobile Architecture',
    category: 'architecture',
    proficiency: 'expert',
    description: 'Local caching, resilient queue synchronization & conflict resolution'
  },
  {
    id: 'api-driven',
    name: 'API-Driven Development',
    category: 'architecture',
    proficiency: 'expert',
    description: 'API-first architecture & contract design'
  },
  {
    id: 'clean-architecture',
    name: 'Clean Architecture Patterns',
    category: 'architecture',
    proficiency: 'advanced',
    description: 'Separation of concerns, domain-driven organization'
  },

  // Programming Languages
  {
    id: 'dart',
    name: 'Dart',
    category: 'languages',
    proficiency: 'expert',
    description: 'Object-oriented language for Flutter mobile development'
  },
  {
    id: 'kotlin',
    name: 'Kotlin',
    category: 'languages',
    proficiency: 'advanced',
    description: 'Modern concise language for Native Android & Compose'
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    category: 'languages',
    proficiency: 'expert',
    description: 'Strong typing, generics, interfaces, strict mode'
  },
  {
    id: 'javascript',
    name: 'JavaScript',
    category: 'languages',
    proficiency: 'expert',
    description: 'Modern ES6+, async/await, closures, prototypes'
  },
  {
    id: 'python',
    name: 'Python',
    category: 'languages',
    proficiency: 'intermediate',
    description: 'Scripting, backend logic, and automation'
  },

  // Tools & Workflow
  {
    id: 'vscode',
    name: 'VS Code',
    category: 'tools',
    proficiency: 'expert',
    description: 'Primary IDE & extension ecosystem'
  },
  {
    id: 'ai-workflow',
    name: 'AI Assisted Workflow',
    category: 'tools',
    proficiency: 'expert',
    description: 'Leveraging AI agents & LLM tools for high efficiency'
  },
  {
    id: 'postman',
    name: 'Postman',
    category: 'tools',
    proficiency: 'advanced',
    description: 'API testing, mock servers, & collection creation'
  },
  {
    id: 'bruno',
    name: 'Bruno',
    category: 'tools',
    proficiency: 'advanced',
    description: 'Git-friendly, offline-first API client'
  },
  {
    id: 'shell',
    name: 'Shell',
    category: 'tools',
    proficiency: 'advanced',
    description: 'Bash / Zsh scripting and terminal operations'
  },

  // Communication & Leadership
  {
    id: 'tech-doc',
    name: 'Technical Documentation',
    category: 'communication',
    proficiency: 'advanced',
    description: 'Clear system specs, API docs, & architecture guides'
  },
  {
    id: 'team-coord',
    name: 'Team Coordination',
    category: 'communication',
    proficiency: 'advanced',
    description: 'Cross-functional team alignment & task breakdown'
  },
  {
    id: 'client-comm',
    name: 'Client Communication',
    category: 'communication',
    proficiency: 'advanced',
    description: 'Requirement gathering & stakeholder management'
  },
  {
    id: 'agile',
    name: 'Agile Development',
    category: 'communication',
    proficiency: 'advanced',
    description: 'Sprints, iterative delivery, and continuous improvement'
  }
]

export const skillCategories: SkillCategory[] = [
  {
    name: 'Frontend Development',
    key: 'frontend',
    description: 'Web & cross-platform mobile interface technologies',
    skills: skills.filter(skill => skill.category === 'frontend')
  },
  {
    name: 'Backend Development',
    key: 'backend',
    description: 'Server-side systems, API engineering, & real-time protocols',
    skills: skills.filter(skill => skill.category === 'backend')
  },
  {
    name: 'Database & Data Management',
    key: 'database',
    description: 'SQL & NoSQL databases, data modeling, & query optimization',
    skills: skills.filter(skill => skill.category === 'database')
  },
  {
    name: 'DevOps & Deployment',
    key: 'devops',
    description: 'Cloud infrastructure, containerization, web servers, & CI/CD pipelines',
    skills: skills.filter(skill => skill.category === 'devops')
  },
  {
    name: 'Software Architecture',
    key: 'architecture',
    description: 'System design, modular structures, clean architecture, & API patterns',
    skills: skills.filter(skill => skill.category === 'architecture')
  },
  {
    name: 'Programming Languages',
    key: 'languages',
    description: 'Core programming languages for web, mobile, & backend development',
    skills: skills.filter(skill => skill.category === 'languages')
  },
  {
    name: 'Tools & Workflow',
    key: 'tools',
    description: 'Development environments, API client tools, & AI-assisted workflows',
    skills: skills.filter(skill => skill.category === 'tools')
  },
  {
    name: 'Communication & Leadership',
    key: 'communication',
    description: 'Technical documentation, team coordination, & Agile practices',
    skills: skills.filter(skill => skill.category === 'communication')
  }
]

// Proficiency level descriptions
export const proficiencyLevels = {
  beginner: {
    label: 'Beginner',
    description: 'Basic understanding and limited practical experience',
    color: 'bg-yellow-500'
  },
  intermediate: {
    label: 'Intermediate',
    description: 'Solid understanding with practical project experience',
    color: 'bg-blue-500'
  },
  advanced: {
    label: 'Advanced',
    description: 'Extensive experience with complex implementations',
    color: 'bg-green-500'
  },
  expert: {
    label: 'Expert',
    description: 'Deep expertise with ability to teach and architect',
    color: 'bg-purple-500'
  }
}
